import React, { useState, useMemo, useCallback, useEffect } from 'react'
import { Select, Input, SearchInput, Tooltip, Button } from '@baseV2'
import { strings } from '@constants'
import {
	FieldsRow,
	FieldItem,
	TableCard,
	ProductsCardHeader,
	ProductsTitleGroup,
	ProductsTitle,
	ProductsCount,
	TableHeaderRow,
	TableHeaderCell,
	TableRow,
	TableCell,
	DescriptionPreview,
	DescriptionPreviewText,
} from './styles'
import { parseNumberValue, uppercaseTrimmedChangeHandler, weightLabel, getDuplicateGradeRowIds, isSameProductGrade } from './inputAdapters'
import BrandsField from './BrandsField'
import RowActionMenu from './RowActionMenu'
import DescriptionEditor from './DescriptionEditor'
import ProductItemsSkeleton from './ProductItemsSkeleton'

// The numeric columns hold typed values, so they get the width back that the product name and the
// over-wide actions column were taking — a 3-4 digit quantity / rate has to stay readable.
const COLUMN_FLEX = {
	name: '3 1 0%',
	grade: '1.2 1 0%',
	weight: '1.6 1 0%',
	price: '1.6 1 0%',
	action: '0 0 2.5rem',
}

// Floor for the typed columns so they never squeeze a number out of view on a narrow drawer.
const NUMERIC_MIN_WIDTH = '4.5rem'

// Memoized single row. Receives its item plus already-computed primitive flags and the
// stable handlers, so editing one row (which only changes that row's `item` reference)
// re-renders just this row — every other row bails out of React's shallow prop compare.
const TmtProductRow = React.memo(function TmtProductRow({
	item,
	index,
	isDuplicate,
	gradeDisabled,
	rowDisabled,
	onCellChange,
	onAddGrade,
	onRemoveGrade,
	isMenuOpen,
	onToggleMenu,
	onCloseMenu,
	isDescriptionEditing,
	onOpenDescription,
	onSaveDescription,
	onCancelDescription,
}) {
	return (
		<>
			<TableRow>
				<TableCell flex={COLUMN_FLEX.name}>
					<Tooltip content={item?.product_name} block>
						<Input type='text' disabled value={item?.product_name} />
					</Tooltip>
				</TableCell>
				<TableCell flex={COLUMN_FLEX.grade}>
					<Input
						type='text'
						disabled={gradeDisabled}
						value={item?.grade}
						error={isDuplicate ? strings('duplicate_grade') : undefined}
						onChange={uppercaseTrimmedChangeHandler((value) => onCellChange({ ...item, grade: value }, index))}
					/>
				</TableCell>
				<TableCell flex={COLUMN_FLEX.weight} minWidth={NUMERIC_MIN_WIDTH}>
					<Input
						type='number'
						disabled={rowDisabled}
						value={item?.required_weight}
						onChange={(e) => onCellChange({ ...item, required_weight: parseNumberValue(e) }, index)}
					/>
				</TableCell>
				<TableCell flex={COLUMN_FLEX.price} minWidth={NUMERIC_MIN_WIDTH}>
					<Input
						type='number'
						disabled={rowDisabled}
						value={item?.target_price}
						onChange={(e) => onCellChange({ ...item, target_price: parseNumberValue(e) }, index)}
					/>
				</TableCell>
				<TableCell flex={COLUMN_FLEX.action} align='center'>
					<RowActionMenu
						item={item}
						index={index}
						isOpen={isMenuOpen}
						onToggle={() => onToggleMenu(item._rowId)}
						onClose={onCloseMenu}
						onAddGrade={onAddGrade}
						onRemoveGrade={onRemoveGrade}
						onEditDescription={() => onOpenDescription(item._rowId)}
					/>
				</TableCell>
			</TableRow>
			{isDescriptionEditing ? (
				<DescriptionEditor
					value={item?.message}
					onSave={(text) => onSaveDescription(item, index, text)}
					onCancel={onCancelDescription}
				/>
			) : item?.message ? (
				<DescriptionPreview>
					<Tooltip content={item.message}>
						<DescriptionPreviewText>{item.message}</DescriptionPreviewText>
					</Tooltip>
				</DescriptionPreview>
			) : null}
		</>
	)
})

const TmtProductTable = ({
	brands,
	inputType,
	preferredUnits,
	grade,
	targetPrice,
	tmtProduct,
	enquiryItems,
	onChange,
	tmtProductHandler,
	addGradeRow,
	removeGradeRow,
	addUnknownProductRow,
	productsLoading,
	user,
}) => {
	const [searchValue, setSearchValue] = useState('')
	const [openMenuRowId, setOpenMenuRowId] = useState(null)
	const [editingDescriptionRowId, setEditingDescriptionRowId] = useState(null)

	const handleToggleMenu = useCallback((rowId) => setOpenMenuRowId((prev) => (prev === rowId ? null : rowId)), [])
	const handleCloseMenu = useCallback(() => setOpenMenuRowId(null), [])
	const handleOpenDescription = useCallback((rowId) => {
		setOpenMenuRowId(null)
		setEditingDescriptionRowId(rowId)
	}, [])
	const handleCancelDescription = useCallback(() => setEditingDescriptionRowId(null), [])
	const handleSaveDescription = useCallback(
		(item, index, text) => {
			tmtProductHandler({ ...item, message: text }, index)
			setEditingDescriptionRowId(null)
		},
		[tmtProductHandler]
	)

	// Guards against a row that was mid-edit/mid-menu disappearing from under us
	// (e.g. "Remove Grade" removing the very row whose description panel was open).
	useEffect(() => {
		const rows = tmtProduct ?? []
		if (editingDescriptionRowId && !rows.some((row) => row._rowId === editingDescriptionRowId)) {
			setEditingDescriptionRowId(null)
		}
		if (openMenuRowId && !rows.some((row) => row._rowId === openMenuRowId)) {
			setOpenMenuRowId(null)
		}
	}, [tmtProduct, editingDescriptionRowId, openMenuRowId])

	const duplicateRowIds = useMemo(() => getDuplicateGradeRowIds(tmtProduct), [tmtProduct])
	const productCount = (tmtProduct ?? []).length
	const hasUnknownProduct = useMemo(() => (tmtProduct ?? []).some((item) => item?.is_unknown_product), [tmtProduct])
	const filteredProducts = useMemo(() => {
		const indexed = (tmtProduct ?? []).map((item, index) => ({ item, index }))
		if (!searchValue) {
			// Once an unknown product has been added, clearing the search shouldn't dump the
			// user back into the full category catalog — keep showing just the added row(s).
			return hasUnknownProduct ? indexed.filter(({ item }) => item?.is_unknown_product) : indexed
		}
		const query = searchValue.toLowerCase()
		return indexed.filter(({ item }) => item?.product_name?.toLowerCase().includes(query))
	}, [tmtProduct, searchValue, hasUnknownProduct])
	const hasNoMatches = Boolean(searchValue) && filteredProducts.length === 0

	const handleAddUnknownProduct = useCallback(() => {
		const name = searchValue.trim().toUpperCase()
		if (!name) return
		addUnknownProductRow(name)
		setSearchValue('')
	}, [searchValue, addUnknownProductRow])

	return (
		<>
			<FieldsRow>
				<FieldItem>
					<Select
						id='selected-unit'
						label={inputType?.label}
						placeholder={inputType?.placeholder}
						data={preferredUnits}
						displayKey='label'
						primaryKey='key'
						value={preferredUnits.find((item) => item.key === inputType.value) ?? {}}
						onChange={onChange('inputType')}
					/>
				</FieldItem>
				<BrandsField brands={brands} onChange={onChange('brands')} user={user} />
			</FieldsRow>

			{productsLoading ? (
				<ProductItemsSkeleton columnFlex={COLUMN_FLEX} weightLabel={weightLabel(inputType)} priceLabel={targetPrice?.label} />
			) : (
				tmtProduct && (
					<TableCard>
						<ProductsCardHeader>
							<ProductsTitleGroup>
								<ProductsTitle>{strings('product_name')}</ProductsTitle>
								<ProductsCount>({productCount})</ProductsCount>
							</ProductsTitleGroup>
							<SearchInput
								value={searchValue}
								onChange={(e) => setSearchValue(e.target.value)}
								placeholder={strings('search_product')}
								maxWidth='22rem'
							/>
							{hasNoMatches && (
								<Button size='small' variant='outline' color='primary' onClick={handleAddUnknownProduct}>
									{strings('add')}
								</Button>
							)}
						</ProductsCardHeader>
						<TableHeaderRow>
							<TableHeaderCell flex={COLUMN_FLEX.name}>{strings('product', 'name')}</TableHeaderCell>
							<TableHeaderCell flex={COLUMN_FLEX.grade}>{strings('grade')}</TableHeaderCell>
							<TableHeaderCell flex={COLUMN_FLEX.weight} minWidth={NUMERIC_MIN_WIDTH}>
								{weightLabel(inputType)}
							</TableHeaderCell>
							<TableHeaderCell flex={COLUMN_FLEX.price} minWidth={NUMERIC_MIN_WIDTH}>
								{targetPrice?.label}
							</TableHeaderCell>
							<TableHeaderCell flex={COLUMN_FLEX.action} align='center'>
								{strings('action')}
							</TableHeaderCell>
						</TableHeaderRow>
						{filteredProducts.map(({ item, index }) => {
							const rowDisabled = (enquiryItems ?? []).some((item2) => isSameProductGrade(item, item2))
							return (
								<TmtProductRow
									key={item._rowId ?? index}
									item={item}
									index={index}
									isDuplicate={duplicateRowIds.has(item._rowId)}
									gradeDisabled={grade?.disabled}
									rowDisabled={rowDisabled}
									onCellChange={tmtProductHandler}
									onAddGrade={addGradeRow}
									onRemoveGrade={removeGradeRow}
									isMenuOpen={openMenuRowId === item._rowId}
									onToggleMenu={handleToggleMenu}
									onCloseMenu={handleCloseMenu}
									isDescriptionEditing={editingDescriptionRowId === item._rowId}
									onOpenDescription={handleOpenDescription}
									onSaveDescription={handleSaveDescription}
									onCancelDescription={handleCancelDescription}
								/>
							)
						})}
					</TableCard>
				)
			)}
		</>
	)
}

export default React.memo(TmtProductTable)
